// The look resolver (themes.md §12, R814).
//
// The six theme picks of the settings, the entitlement set the client saw
// last and the cached custom theme bodies go in; ONE `Look` comes out. Every
// reader of a look reads the `Look` and never the picks: the map frame, the
// spawn start, the scene, the arcade match, the chrome theme and the sound
// director.
//
// This module is pure. It reads no store and no network, so every rule of
// the resolver is a unit test (`look.test.ts`). `ui/themestate.ts` feeds it
// from the two caches of `ui/lookcache.ts`.
//
// **The field names are the names of the theme bodies** (themes.md §3), so
// a body read from the meta server, a vendored theme and the resolved look
// use one vocabulary. The preview's `PreviewLook` uses it too, and
// `lookToPreview` adapts one to the other.
//
// **The rules** (R807, R809):
//   - Without `ui_themes`, every CUSTOM pick resolves to AUTO. The resolver
//     writes no setting, so the stored picks come back when the key comes
//     back.
//   - A vendored entry of any category needs its own `key` and no other: an
//     entry with no `key` is free (§2, §4.2). A keyed pick without its key
//     resolves to AUTO, or to the free default.
//   - A vendored UI pick is a faction choice or a vendored UI theme. A
//     vendored UI theme is a body, so `look.ui` carries it as it carries a
//     custom body.
//   - A custom pick whose body is not cached, is marked missing, belongs to
//     another category or does not parse resolves to AUTO.
//   - A custom body is read field by field: a World or a Marks field that
//     names an id the build does not carry takes the AUTO value of that
//     field, and a Chart body keeps only the keys `CHART_THEME_TUNABLE`
//     names.

import {
  KEY_UI_THEMES, THEME_CATEGORIES, THEME_MARKS_FIELDS, THEME_UI_ALPHAS,
  THEME_UI_TOKENS, THEME_WORLD_FIELDS, type ThemeCategory,
} from '../gen/balance';
import {
  CHART, CHART_THEME_TUNABLE, VENDORED_THEMES, type ChartThemeOverlay,
} from '../gen/presentation';
import { parseClipMap, parseVoice, type SfxVoice } from '../audio/manifest';
import { CUSTOM, type ChartSetting, type GameSettings, type VendoredUIId } from './settings';
import { THEME_CHOICES, resolveTheme, themeBody, type ThemeBody, type ThemeChoice } from './theme';
import { themeKey, type CachedTheme } from './lookcache';
import { isObject } from '../net/json';
import { HULL_FIELDS, readSceneLook, type SceneLook } from '../render/scene-look';
import type { MarksHullField } from './settings';
import type { LayerView } from './layerview';
import type { ChartPieceOverlay } from '../fast/chart/furniture';
import { territoryLayerView, type PreviewLook } from '../preview/look';

// --- the body shapes (themes.md §3) -----------------------------------------

type Fields<T extends Record<string, readonly string[]>> = { -readonly [K in keyof T]: T[K][number] };

/** A World body: one look id per field (themes.md §3.4). */
export type WorldBody = Fields<typeof THEME_WORLD_FIELDS>;
/** A Marks body: one look id per field (themes.md §3.5). */
export type MarksBody = Fields<typeof THEME_MARKS_FIELDS>;
/** A Chart body: the overlay on the piece table (themes.md §3.6). */
export interface ChartBody { pieces: ChartThemeOverlay }
/** A Music body: manifest track URLs in play order (themes.md §3.2). */
export interface MusicBody { tracks: string[]; shuffle: boolean }
/** An SFX body: the manifest SFX theme minus its id and title (§3.3). */
export interface SfxBody {
  map: Record<string, string[]>;
  voice: SfxVoice;
}

/** The pick in force per category: a vendored id, '' for the manifest
 *  default of Music and SFX, or `custom`. */
export interface LookPicks {
  ui: ThemeChoice | VendoredUIId | typeof CUSTOM;
  music: string;
  sfx: string;
  world: string;
  marks: string;
  chart: string;
}

/** One resolved look. */
export interface Look {
  /** What each category resolved to. `custom` only where a body is in force. */
  picks: LookPicks;
  /** The theme code in force, per category that resolved to `custom`. */
  codes: Partial<Record<ThemeCategory, string>>;
  /** The UI body of a custom or a vendored UI theme, or null: the faction
   *  choice `picks.ui` is in force. */
  ui: ThemeBody | null;
  /** The custom Music body, or null: the playlist `picks.music` plays. */
  music: MusicBody | null;
  /** The custom SFX body, or null: the SFX theme `picks.sfx` sounds. */
  sfx: SfxBody | null;
  world: WorldBody;
  marks: MarksBody;
  chart: ChartBody;
  /** The categories whose stored pick is suspended: a CUSTOM or a keyed
   *  pick without its key (R809). */
  suspended: ThemeCategory[];
  /** The categories whose stored custom pick has no usable body. */
  unresolved: ThemeCategory[];
}

/** One playlist or SFX theme of the manifest, as the resolver needs it. */
export interface AudioEntry { id: string; key?: string }

export interface LookInput {
  settings: GameSettings;
  /** The entitlement keys the account holds (or held last). */
  held: ReadonlySet<string>;
  /** The cached custom themes, by code. */
  themes: Readonly<Record<string, CachedTheme>>;
  /** The manifest's playlists and SFX themes, when the manifest is known. A
   *  keyed audio entry is refused here only then; the sound director applies
   *  the same rule with the manifest it holds. */
  audio?: { playlists: readonly AudioEntry[]; themes: readonly AudioEntry[] } | null;
}

// --- AUTO -------------------------------------------------------------------

const AUTO_WORLD = VENDORED_THEMES.world[0];
const AUTO_MARKS = VENDORED_THEMES.marks[0];

/** The World AUTO: today's look (themes.md §4.1), as a fresh object. */
export function autoWorld(): WorldBody {
  const { id: _i, key: _k, ...body } = AUTO_WORLD;
  return body;
}

/** The Marks AUTO: today's look (themes.md §4.1), as a fresh object. */
export function autoMarks(): MarksBody {
  const { id: _i, key: _k, ...body } = AUTO_MARKS;
  return body;
}

/** The look a player who picked nothing sees. */
export function autoLook(): Look {
  return {
    picks: { ui: 'auto', music: '', sfx: '', world: 'auto', marks: 'auto', chart: 'auto' },
    codes: {},
    ui: null,
    music: null,
    sfx: null,
    world: autoWorld(),
    marks: autoMarks(),
    chart: { pieces: {} },
    suspended: [],
    unresolved: [],
  };
}

// --- the body readers -------------------------------------------------------

const HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * A UI body, or null where a token or an alpha is out of shape. The body is
 * `{tokens, alphas}`, the shape of a vendored UI theme without its id and
 * key (§3.1, §11.3); the answer is the flat `ThemeBody` of `ui/theme.ts`.
 */
export function readUIBody(v: unknown): ThemeBody | null {
  if (!isObject(v) || !isObject(v.tokens) || !isObject(v.alphas)) return null;
  const out: Record<string, string | number> = {};
  for (const t of THEME_UI_TOKENS) {
    const c = v.tokens[t];
    if (typeof c !== 'string' || !HEX.test(c)) return null;
    out[t] = c.toLowerCase();
  }
  for (const a of THEME_UI_ALPHAS) {
    const n = v.alphas[a];
    if (typeof n !== 'number' || !(n > 0 && n <= 1)) return null;
    out[a] = n;
  }
  return out as unknown as ThemeBody;
}

/** A Music body, or null where it holds no track (§3.2). */
export function readMusicBody(v: unknown): MusicBody | null {
  if (!isObject(v) || !Array.isArray(v.tracks)) return null;
  const tracks = v.tracks.filter((t): t is string => typeof t === 'string' && t !== '');
  if (tracks.length === 0) return null;
  return { tracks, shuffle: v.shuffle === true };
}

/** An SFX body, read through the manifest's own clip parsers (§3.3). The
 *  voice timing sits inside `voice`, as in the manifest (R813). */
export function readSfxBody(v: unknown): SfxBody | null {
  if (!isObject(v)) return null;
  return { map: parseClipMap(v.map), voice: parseVoice(v.voice) };
}

/** Reads a field body over its AUTO, field by field (§3.4, §3.5). */
function readFields<T extends Record<string, string>>(
  v: unknown, fields: Record<keyof T & string, readonly string[]>, auto: T,
): T | null {
  if (!isObject(v)) return null;
  const out = { ...auto };
  for (const f of Object.keys(fields) as (keyof T & string)[]) {
    const id = v[f];
    if (typeof id === 'string' && fields[f].includes(id)) (out as Record<string, string>)[f] = id;
  }
  return out;
}

export function readWorldBody(v: unknown): WorldBody | null {
  const out = readFields<WorldBody>(v, THEME_WORLD_FIELDS, autoWorld());
  if (out === null || !isObject(v)) return out;
  // The legacy move (R2869): a body stored before the split names a heat
  // look in `currents`. With no valid `currentsHeat`, that look goes to
  // `currentsHeat`, and `currents` keeps its AUTO from `readFields`.
  // compat(R2869, 2027-02-02)
  const heats: readonly string[] = THEME_WORLD_FIELDS.currentsHeat;
  const cur = v.currents;
  const heat = v.currentsHeat;
  if (typeof cur === 'string' && heats.includes(cur)
    && !(typeof heat === 'string' && heats.includes(heat))) {
    out.currentsHeat = cur as WorldBody['currentsHeat'];
  }
  return out;
}

export function readMarksBody(v: unknown): MarksBody | null {
  return readFields<MarksBody>(v, THEME_MARKS_FIELDS, autoMarks());
}

/**
 * A Chart body (§3.6): only a piece the table carries, only the two
 * readings, and only a key `CHART_THEME_TUNABLE` names, with a boolean.
 */
export function readChartBody(v: unknown): ChartBody | null {
  if (!isObject(v) || !isObject(v.pieces)) return null;
  const pieces: Record<string, Record<string, Record<string, boolean>>> = {};
  for (const [slug, byReading] of Object.entries(v.pieces)) {
    if (!(slug in CHART.pieces) || !isObject(byReading)) continue;
    for (const reading of ['historical', 'modern'] as const) {
      const keys = byReading[reading];
      if (!isObject(keys)) continue;
      for (const k of CHART_THEME_TUNABLE) {
        if (typeof keys[k] !== 'boolean') continue;
        ((pieces[slug] ??= {})[reading] ??= {})[k] = keys[k] as boolean;
      }
    }
  }
  return { pieces: pieces as ChartThemeOverlay };
}

// --- the resolver -----------------------------------------------------------

/** The settings field that holds the pick of each category. */
export const PICK_FIELD = {
  ui: 'theme', music: 'playlist', sfx: 'sfxTheme',
  world: 'world', marks: 'marks', chart: 'chartTheme',
} as const satisfies Record<ThemeCategory, keyof GameSettings>;

/** The pick a category holds in the settings. */
export function storedThemePick(s: GameSettings, c: ThemeCategory): string {
  return String(s[PICK_FIELD[c]]);
}

/** The reader of each category's body. A `Look` keeps the body under the
 *  category word, so one table serves the custom and the vendored pick. */
const READ_BODY: Record<ThemeCategory, (v: unknown) => unknown> = {
  ui: readUIBody, music: readMusicBody, sfx: readSfxBody,
  world: readWorldBody, marks: readMarksBody, chart: readChartBody,
};

/** Resolves the settings, the entitlement set and the bodies to one look. */
export function resolveLook(input: LookInput): Look {
  const { settings: s, held, themes } = input;
  const look = autoLook();
  // The body and the pick of a category stand under the category word, and
  // `READ_BODY` pairs each word with the reader of its own shape.
  const bodies = look as unknown as Record<ThemeCategory, unknown>;
  const picks = look.picks as Record<ThemeCategory, string>;
  // CUSTOM needs `ui_themes`. A vendored entry needs its own key alone.
  const themed = held.has(KEY_UI_THEMES);
  const allowed = (key: string | undefined): boolean => !key || held.has(key);

  /** The cached body of the category's code, or null (the pick is AUTO). */
  const customBody = (c: ThemeCategory): unknown => {
    if (!themed) { look.suspended.push(c); return null; }
    const code = s.codes[c];
    const t = code ? themes[themeKey(code, c)] : undefined;
    if (!t || t.missing || t.category !== c || t.body === null || t.body === undefined) {
      look.unresolved.push(c);
      return null;
    }
    return t.body;
  };
  /** Takes a parsed custom body, or marks the category unresolved. */
  const take = <T>(c: ThemeCategory, body: T | null): T | null => {
    if (body === null) { look.unresolved.push(c); return null; }
    look.codes[c] = s.codes[c];
    return body;
  };

  for (const c of THEME_CATEGORIES) {
    const pick = storedThemePick(s, c);
    if (pick === CUSTOM) {
      const raw = customBody(c);
      if (raw === null) continue;
      const b = take(c, READ_BODY[c](raw));
      if (b !== null) { bodies[c] = b; picks[c] = CUSTOM; }
      continue;
    }
    // A vendored pick.
    switch (c) {
      case 'ui': {
        // The faction themes are free (§4.2). A vendored UI theme is free
        // unless it carries a key, and its body is in force.
        const entry = VENDORED_THEMES.ui.find((e) => e.id === pick);
        if (entry) {
          if (!allowed(entry.key)) { look.suspended.push(c); break; }
          look.picks.ui = entry.id as VendoredUIId;
          look.ui = readUIBody(entry);
          break;
        }
        look.picks.ui = choiceOf(s.theme);
        break;
      }
      case 'music':
      case 'sfx': {
        const list = c === 'music' ? input.audio?.playlists : input.audio?.themes;
        const entry = list?.find((e) => e.id === pick);
        if (entry && !allowed(entry.key)) { look.suspended.push(c); break; }
        look.picks[c] = pick;
        break;
      }
      case 'world':
      case 'marks':
      case 'chart': {
        if (pick === 'auto') break;
        const entry = (VENDORED_THEMES[c] as readonly { id: string; key: string }[])
          .find((e) => e.id === pick);
        if (!entry) break;
        // A vendored entry needs its own key alone (§2, §4.2).
        if (!allowed(entry.key)) { look.suspended.push(c); break; }
        look.picks[c] = pick;
        // A vendored entry is an object, so its reader always answers.
        bodies[c] = READ_BODY[c](entry) ?? bodies[c];
        break;
      }
    }
  }
  return look;
}

// --- the adapters -----------------------------------------------------------

/**
 * The layer toggles a look starts with (themes.md §9, owner ruling 3). The
 * preview and the map share `territoryLayerView`, so they agree.
 */
export function startLayerView(look: Look): LayerView {
  return territoryLayerView(look.world.territory);
}

/** The Chart overlay the chart furniture takes, or null for AUTO. */
export function chartOverlay(look: Look): ChartPieceOverlay | null {
  return Object.keys(look.chart.pieces).length === 0
    ? null
    : look.chart.pieces as unknown as ChartPieceOverlay;
}

/** The UI body in force: the custom or the vendored UI theme's body, or the
 *  faction choice's own. */
export function uiBody(look: Look, faction: number): ThemeBody {
  if (look.ui) return look.ui;
  return themeBody(resolveTheme(choiceOf(look.picks.ui), faction));
}

/** A pick as a faction choice, or AUTO where it names none. */
function choiceOf(pick: unknown): ThemeChoice {
  return (THEME_CHOICES as readonly unknown[]).includes(pick) ? pick as ThemeChoice : 'auto';
}

/**
 * The canvas half of the look (R814): the fields the map scene draws.
 * `MapScene.setLook` hands it on to the fleet, site and terrain layers.
 */
export function sceneLookOf(look: Look): SceneLook {
  const w = look.world;
  const m = look.marks;
  return readSceneLook({
    terrain: w.terrain, beam: w.beam, aura: w.aura,
    // The current field (R2716). The chart furniture draws it.
    currents: w.currents,
    // The current heat look (R2869). The CURRENTS heat map draws it.
    currentsHeat: w.currentsHeat,
    // The depth look (R2726). The depth pass of the depth tints draws it.
    depth: w.depth,
    // The gun shots and the hits (R1007): three shot looks by the shooter's
    // role, and three hit looks by what the victim still holds.
    shotMelee: w.shotMelee, shotArtillery: w.shotArtillery,
    shotSentinel: w.shotSentinel,
    hitShield: w.hitShield, hitHull: w.hitHull, hitSink: w.hitSink,
    // The hold-fire look (R1439), the tube reload look (R1474) and the
    // detonation look (R2079).
    holdFire: w.holdFire, reload: w.reload, detonation: w.detonation,
    // The Dredger harvesting look (R1120) and the anchoring look (R1125).
    gather: w.gather, anchoring: w.anchoring,
    // The four faction marks (R1568): the second channel of §11.
    factionSite: w.factionSite, factionHull: w.factionHull,
    factionEdge: w.factionEdge, factionKey: w.factionKey,
    // The seven looks of the Sapper part (R2652).
    seaMineDetonation: w.seaMineDetonation, seaMineLay: w.seaMineLay, deployableLay: w.deployableLay,
    seaMineCollect: w.seaMineCollect, deployableCollect: w.deployableCollect,
    // The cache work look of SEARCH (R2833).
    cacheWork: w.cacheWork,
    seaMine: m.seaMine, deployable: m.deployable,
    shield: m.shield, digits: m.digits, deposit: m.deposit,
    ownFleet: m.ownFleet, squadFleet: m.squadFleet,
    ownHold: m.ownHold, squadHold: m.squadHold,
    // The structure symbol (R1103): ONE id for all nine strategic objects.
    structure: m.structure,
    // The map marker look (R1291). The chart furniture draws it.
    mapMarker: m.mapMarker,
    // The point of interest marks (R2714). The chart furniture draws them.
    poi: m.poi,
    // The ship icon set and the fourteen hull fields (R835). The fourteen are
    // copied from one list, so a field added to the roster cannot be missed.
    ship: m.ship, ...hullFieldsOf(m),
  });
}

/** The fourteen hull fields of a Marks body, as the scene reads them (R835). */
function hullFieldsOf(m: MarksBody): Partial<Record<MarksHullField, string>> {
  const out: Partial<Record<MarksHullField, string>> = {};
  for (const f of HULL_FIELDS) out[f] = m[f];
  return out;
}

/**
 * The look as the theme preview takes it (R806). `reading` is the chart
 * reading, which is a DISPLAY setting and not a theme; `faction` is what a
 * vendored UI choice of AUTO follows.
 */
export function lookToPreview(look: Look, reading: ChartSetting, faction: number): PreviewLook {
  const w = look.world;
  const m = look.marks;
  return {
    territory: w.territory, terrain: w.terrain, currents: w.currents, currentsHeat: w.currentsHeat,
    depth: w.depth, spawn: w.spawn,
    torpedo: w.torpedo, damage: w.damage, beam: w.beam, aura: w.aura,
    shotMelee: w.shotMelee, shotArtillery: w.shotArtillery,
    shotSentinel: w.shotSentinel,
    hitShield: w.hitShield, hitHull: w.hitHull, hitSink: w.hitSink,
    holdFire: w.holdFire, reload: w.reload, detonation: w.detonation,
    gather: w.gather, anchoring: w.anchoring,
    // The Sapper part (R2652, R2657).
    seaMineDetonation: w.seaMineDetonation, seaMineLay: w.seaMineLay, deployableLay: w.deployableLay,
    seaMineCollect: w.seaMineCollect, deployableCollect: w.deployableCollect,
    cacheWork: w.cacheWork,
    factionSite: w.factionSite, factionHull: w.factionHull,
    factionEdge: w.factionEdge, factionKey: w.factionKey,
    notices: w.notices, dock: w.dock, banner: w.banner,
    ship: m.ship, ...hullFieldsOf(m),
    ownFleet: m.ownFleet, squadFleet: m.squadFleet,
    shield: m.shield, digits: m.digits, hold: m.hold,
    // The structure symbol set (R1103): ONE id for all nine strategic
    // objects, so the preview takes one field and no fold.
    structure: m.structure,
    ownHold: m.ownHold, squadHold: m.squadHold, deposit: m.deposit,
    // The map marker look (R1291): the preview stages one marker of each
    // kind and draws them in this look.
    mapMarker: m.mapMarker,
    seaMine: m.seaMine, deployable: m.deployable,
    // The point of interest marks (R2714): the preview stages one point of
    // each kind and draws them in this set.
    poi: m.poi,
    chart: reading,
    chartPieces: chartOverlay(look),
    ui: uiBody(look, faction),
  };
}
